import { createGlobalStyle } from 'styled-components';
import {
  FOCUS_RING_COLOR_VARIABLE,
  FOCUS_RING_OFFSET_VARIABLE,
} from '../constants/constants';
import {
  getBrandInkRules,
  getIconColorRules,
  getTextColoredIconRules,
} from './iconColors';
import { componentToken } from './utils';

// Text fields and selects show focus with their own border and glow; menu
// items and tabs draw the ring inside so scrolling lists do not clip it. A
// container can recolor or move the ring with the focus ring variables.
const getFocusRingRules = (color: string) =>
  color &&
  `
    :is(
        a,
        button,
        [role='button'],
        [role='switch'],
        [tabindex]:not([tabindex='-1'])
      ):not(
        input,
        textarea,
        [role='combobox'],
        [role='tab'],
        [role='menuitem'],
        [role='option']
      ):focus-visible {
      outline: 2px solid var(${FOCUS_RING_COLOR_VARIABLE}, ${color});
      outline-offset: var(${FOCUS_RING_OFFSET_VARIABLE}, 1px);
    }

    :is([role='tab'], [role='menuitem'], [role='option']):focus-visible {
      outline: 2px solid var(${FOCUS_RING_COLOR_VARIABLE}, ${color});
      outline-offset: -2px;
    }
  `;

// Progress indicators (`role="progressbar"`, as MUI's spinners and bars
// have) keep moving, since their motion is what shows that something is
// loading.
const REDUCED_MOTION_TARGETS =
  ":not([role='progressbar'], [role='progressbar'] *)";

const REDUCED_MOTION_RULES = `
  @media (prefers-reduced-motion: reduce) {
    ${REDUCED_MOTION_TARGETS},
    ${REDUCED_MOTION_TARGETS}::before,
    ${REDUCED_MOTION_TARGETS}::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }
`;

const GlobalStyle = createGlobalStyle`
  *,
  *:before,
  *:after {
    box-sizing: border-box;
    font-family: ${componentToken('globalStyle.fontFamily')};
    font-feature-settings: ${componentToken('globalStyle.fontFeatureSettings')};
  }

  code,
  pre,
  kbd,
  samp,
  tt {
    font-family: ${componentToken('globalStyle.codeFontFamily')};
  }

  body {
    margin: 0;
    background-color: ${componentToken('globalStyle.pageBackground')};
    color: ${componentToken('globalStyle.textColor')};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow-y: hidden;
  }

  #root {
    width: 100vw;
    height: 100vh;
  }

  * {
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;
  }

  *:hover {
    scrollbar-color: ${componentToken(
      'globalStyle.scrollbarThumb',
    )} transparent;
  }

  ${({ theme }) => getIconColorRules(theme.tokens.svgPaints)}

  ${({ theme }) => getBrandInkRules(theme.tokens.svgPaints.brandInk)}

  ${({ theme }) =>
    theme.tokens.components.iconButton.defaultColor &&
    getTextColoredIconRules('.MuiIconButton-root', theme)}

  ${({ theme }) =>
    theme.tokens.components.globalStyle.honorReducedMotion &&
    REDUCED_MOTION_RULES}

  ${({ theme }) =>
    getFocusRingRules(theme.tokens.components.globalStyle.focusRing)}
`;

export default GlobalStyle;
